import { useState, type ReactNode } from 'react';
import { Link } from '@tanstack/react-router';
import { LogIn, LogOut, Moon, Sun } from 'lucide-react';
import { NAV } from '@/nav';
import { api, loginUrl } from '@/lib/api';
import { useSession } from '@/lib/session';
import { Button } from './ui/primitives';

type Theme = 'light' | 'dark';

function currentTheme(): Theme {
  const explicit = document.documentElement.dataset.theme;
  if (explicit === 'light' || explicit === 'dark') return explicit;
  return window.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

function ThemeToggle() {
  const [theme, setTheme] = useState<Theme>(currentTheme);
  const next: Theme = theme === 'dark' ? 'light' : 'dark';
  return (
    <button
      type="button"
      onClick={() => {
        document.documentElement.dataset.theme = next;
        try {
          localStorage.setItem('agentic-os.theme', next);
        } catch {
          /* storage unavailable: theme still applies for this session */
        }
        setTheme(next);
      }}
      className="inline-flex size-8 items-center justify-center rounded-md text-muted hover:bg-surface-muted hover:text-fg"
      aria-label={`Switch to ${next} theme`}
    >
      {theme === 'dark' ? (
        <Sun className="size-4" aria-hidden />
      ) : (
        <Moon className="size-4" aria-hidden />
      )}
    </button>
  );
}

const LOGIN_ERRORS: Record<string, string> = {
  expired: 'The sign-in attempt expired. Please try again.',
  failed: 'Sign-in failed. Please try again.',
  no_membership:
    'You are signed in, but your account has no membership in an Agentic OS organization. Ask an administrator.',
};

function SignIn() {
  const code = new URLSearchParams(window.location.search).get('login_error');
  return (
    <div className="flex h-full items-center justify-center p-6">
      <div className="w-full max-w-sm rounded-lg border border-border bg-surface p-6 text-center shadow-xs">
        <img src="/favicon.svg" alt="" className="mx-auto size-10" />
        <h1 className="mt-3 text-lg font-semibold">Agentic OS Admin</h1>
        <p className="mt-1 text-sm text-muted">Sign in with your organization account.</p>
        {code && LOGIN_ERRORS[code] && (
          <p role="alert" className="mt-4 rounded-md bg-fail-bg px-3 py-2 text-sm text-fail">
            {LOGIN_ERRORS[code]}
          </p>
        )}
        <a
          href={loginUrl(
            window.location.pathname === '/' ? '/dashboard' : window.location.pathname,
          )}
          className="mt-5 inline-flex items-center gap-2 rounded-md bg-accent px-4 py-2 text-sm font-medium text-accent-contrast hover:opacity-90"
        >
          <LogIn className="size-4" aria-hidden />
          Sign in
        </a>
      </div>
    </div>
  );
}

export function AppShell({ children }: { children: ReactNode }) {
  const { session, loading } = useSession();
  if (loading) return <div className="p-6 text-sm text-muted">Loading…</div>;
  if (!session) return <SignIn />;

  const logout = async () => {
    const r = await api<{ logoutUrl: string }>('/api/v1/auth/logout', { method: 'POST' }).catch(
      () => null,
    );
    window.location.href = r?.logoutUrl ?? '/';
  };

  return (
    <div className="flex h-full min-h-0">
      <aside className="hidden w-60 shrink-0 flex-col border-r border-border bg-surface md:flex">
        <div className="flex h-14 items-center gap-2 border-b border-border px-4">
          <img src="/favicon.svg" alt="" className="size-6" />
          <span className="text-sm font-semibold tracking-tight">Agentic OS</span>
          <span className="ml-auto rounded bg-neutral-bg px-1.5 py-0.5 text-[10px] font-medium text-muted">
            ADMIN
          </span>
        </div>
        <nav aria-label="Primary" className="flex-1 overflow-y-auto px-2 py-3">
          {NAV.map((group) => (
            <div key={group.label} className="mb-4">
              <div className="px-2 pb-1 text-[11px] font-medium tracking-wide text-muted uppercase">
                {group.label}
              </div>
              <ul>
                {group.items.map((item) => (
                  <li key={item.path}>
                    <Link
                      to={item.path}
                      className="flex items-center gap-2.5 rounded-md px-2 py-1.5 text-sm text-muted hover:bg-surface-muted hover:text-fg"
                      activeProps={{ className: 'bg-surface-muted text-fg font-medium' }}
                    >
                      <item.icon className="size-4 shrink-0" aria-hidden />
                      <span className="truncate">{item.label}</span>
                      {!item.available && (
                        <span
                          className="ml-auto text-[10px] text-muted"
                          title={`Planned for Increment ${item.increment}`}
                        >
                          I{item.increment}
                        </span>
                      )}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </nav>
      </aside>
      <div className="flex min-w-0 flex-1 flex-col">
        <header className="flex h-14 items-center gap-3 border-b border-border bg-surface px-4 md:px-6">
          <span className="text-sm font-semibold md:hidden">Agentic OS</span>
          <span className="truncate text-xs text-muted">
            {session.organization?.name} · {session.roles.join(', ')}
          </span>
          <div className="ml-auto flex items-center gap-2">
            <Link
              to="/session"
              className="hidden text-sm hover:underline sm:inline"
              data-testid="current-user"
            >
              {session.principal.email ?? session.principal.displayName}
            </Link>
            <ThemeToggle />
            <Button onClick={() => void logout()} aria-label="Sign out">
              <LogOut className="size-3.5" aria-hidden />
              <span className="hidden sm:inline">Sign out</span>
            </Button>
          </div>
        </header>
        <main className="min-h-0 flex-1 overflow-y-auto px-4 py-6 md:px-8">{children}</main>
      </div>
    </div>
  );
}
